import { createAsyncThunk } from '@reduxjs/toolkit';
import { PaymentMethod } from '@wix/cashier-common/dist/src/enums/payments/PaymentMethod';
import { t } from '../../utils/i18n';
import { type BiLogger } from '../../components/Provider/BiLogger/BiLogger';
import { getShippingOptions } from '../../components/payment-methods/GooglePay/utils/getShippingOptions';
import { selectTopProps } from '../../selectors';
import { getDisplayItems } from '../../components/payment-methods/GooglePay/utils/getTransactionInfo';
import { getShippingContactError } from '../../components/payment-methods/GooglePay/utils/getShippingContactError';
import { type GooglePayButtonConfig } from '../../components/payment-methods/GooglePay/types';
import { isValidShippingError, UNEXPECTED_ERROR } from '../../types/Shipping';
import { type StoreState } from '../../types/StoreState';
import { amountToLong } from '../../utils/amountToLong';
import { getDefaultShippingOption } from '../../components/payment-methods/GooglePay/utils/getDefaultShippingOption';
import { type OnShippingMethodSelected } from '../../components/payment-methods/GooglePay/callbacks/createOnShippingMethodSelected';
import { spbShippingMethodChanged, spbShippingMethodNotAccepted } from '../../utils/bi/events/biEvents';
import { selectGooglePayMetadata } from '../../selectors/domain';
import { setGooglePayPaymentDataShippingMethods } from './common';

export type ShippingAddress = Parameters<GooglePayButtonConfig['onPaymentDataChanged']>[0]['shippingAddress'];

type OnShippingContactSelectedParams = {
  transactionInfo: google.payments.api.TransactionInfo;
  biLogger: BiLogger;
  shippingAddress: ShippingAddress;
  onShippingMethodSelected: OnShippingMethodSelected;
};

export type OnShippingContactSelectedReturn = ReturnType<GooglePayButtonConfig['onPaymentDataChanged']>;

export const onShippingContactSelected = createAsyncThunk<
  OnShippingContactSelectedReturn,
  OnShippingContactSelectedParams,
  {
    state: StoreState;
  }
>(
  'GOOGLE_ON_SHIPPING_CONTACT_SELECTED',
  async ({ transactionInfo, shippingAddress, biLogger, onShippingMethodSelected }, { dispatch, getState }) => {
    const state = getState();
    const topProps = selectTopProps(state);
    const methodMetadata = selectGooglePayMetadata(state);
    const { locale, currency, requestShipping, meta } = topProps;
    try {
      const updates = await topProps.onShippingContactSelected({
        countryCode: shippingAddress.countryCode,
        state: shippingAddress.administrativeArea,
        city: shippingAddress.locality,
        postalCode: shippingAddress.postalCode,
      });
      const { error, paymentAmount } = updates;
      const isError = isValidShippingError(error);
      const totalPrice = isError ? transactionInfo.totalPrice : paymentAmount;
      const isNeedToUpdateShippingOptions = !isError && updates.shippingMethods;

      if (isNeedToUpdateShippingOptions) {
        dispatch(setGooglePayPaymentDataShippingMethods(updates.shippingMethods));
      }

      if (!isError) {
        biLogger.report(spbShippingMethodChanged, {
          fieldChanged: 'shippingContact',
          amountLong: amountToLong(paymentAmount),
          appInstanceId: meta.appInstanceId,
          currency: transactionInfo.currencyCode,
          externalOrderId: transactionInfo.transactionId,
          paymentMethod: PaymentMethod.GooglePay,
          paymentProvider: methodMetadata.providerId,
          isPartner: undefined as any, // TODO[bi]
          amount: undefined as any, // TODO[bi]
          orderSnapshotId: undefined as any, // TODO[bi]
          transactionId: undefined as any, // TODO[bi]
        });
      }

      if (isError) {
        biLogger.report(spbShippingMethodNotAccepted, {
          fieldChanged: 'shippingContact',
          amountLong: amountToLong(paymentAmount),
          appInstanceId: meta.appInstanceId,
          currency: transactionInfo.currencyCode,
          externalOrderId: transactionInfo.transactionId,
          paymentMethod: PaymentMethod.GooglePay,
          paymentProvider: methodMetadata.providerId,
          errorReason: getShippingContactError(error).reason,
          isPartner: undefined as any, // TODO[bi]
          amount: undefined as any, // TODO[bi]
          orderSnapshotId: undefined as any, // TODO[bi]
          transactionId: undefined as any, // TODO[bi]
        });
      }

      const newShippingContactTransactionInfo = {
        ...(isError && { error: getShippingContactError(error) }),
        newTransactionInfo: {
          ...transactionInfo,
          totalPrice,
          ...(updates.paymentBreakdown && {
            displayItems: getDisplayItems({
              paymentBreakdown: updates.paymentBreakdown,
              requestShipping,
              orderItems: updates.orderItems || topProps.orderItems, // TODO: remove this after ECOM integration
            }),
          }),
        },
        ...(isNeedToUpdateShippingOptions && {
          newShippingOptionParameters: {
            shippingOptions: getShippingOptions({
              shippingMethods: updates.shippingMethods,
              locale,
              currencyCode: currency,
              isRequired: requestShipping,
            }),
          },
        }),
      };

      if (newShippingContactTransactionInfo.error) {
        return newShippingContactTransactionInfo;
      }

      if (!newShippingContactTransactionInfo.newShippingOptionParameters?.shippingOptions?.length) {
        return newShippingContactTransactionInfo;
      }

      const shippingMethodUpdates = await onShippingMethodSelected({
        id: getDefaultShippingOption(newShippingContactTransactionInfo.newShippingOptionParameters.shippingOptions).id,
      });

      const isShippingMethodError = shippingMethodUpdates.error;
      const totalPriceWithNewShippingOption = isShippingMethodError
        ? totalPrice
        : shippingMethodUpdates.newTransactionInfo.totalPrice;

      return {
        ...(isShippingMethodError && { error: shippingMethodUpdates.error }),
        ...(newShippingContactTransactionInfo.newShippingOptionParameters && {
          newShippingOptionParameters: newShippingContactTransactionInfo.newShippingOptionParameters,
        }),
        ...{
          newTransactionInfo: {
            ...newShippingContactTransactionInfo.newTransactionInfo,
            ...shippingMethodUpdates.newTransactionInfo,
            totalPrice: totalPriceWithNewShippingOption,
          },
        },
      };
    } catch (mainError) {
      biLogger.report(spbShippingMethodNotAccepted, {
        fieldChanged: 'shippingContact',
        appInstanceId: meta.appInstanceId,
        currency: transactionInfo.currencyCode,
        externalOrderId: transactionInfo.transactionId,
        paymentMethod: PaymentMethod.GooglePay,
        paymentProvider: methodMetadata.providerId,
        errorReason: `${UNEXPECTED_ERROR}: ${typeof mainError === 'string' ? mainError : JSON.stringify(mainError)}`,
        amountLong: undefined as any, // TODO[bi]
        isPartner: undefined as any, // TODO[bi]
        amount: undefined as any, // TODO[bi]
        orderSnapshotId: undefined as any, // TODO[bi]
        transactionId: undefined as any, // TODO[bi]
      });

      return {
        newTransactionInfo: transactionInfo,
        error: {
          reason: 'OTHER_ERROR',
          intent: 'SHIPPING_ADDRESS',
          message: t('pm.shipping.unknownError'),
        },
      };
    }
  },
);
